// sketchArrangement — планарное разбиение эскиза на грани (как во Fusion 360).
//
// Проблема старого findProfiles: он искал циклы в графе, где рёбра соединяются
// только по ОБЩИМ ТОЧКАМ (p1/p2). Если кривые ПЕРЕСЕКАЮТСЯ геометрически, но не
// имеют общей вершины (спица пересекает окружность-обод; малый круг пересекает
// спицу), — области между ними НЕ находились. Кликался только цельный круг.
//
// Решение (как в настоящем CAD): строим arrangement всех кривых —
//   1) аппроксимируем каждую кривую полилинией (дуга/окружность → отрезки);
//   2) находим ВСЕ попарные пересечения отрезков, дробим их в точках пересечения;
//   3) склеиваем близкие вершины;
//   4) обходим минимальные грани планарного графа (DCEL, «самый правый поворот»);
//   5) каждая внутренняя грань = профиль-полигон.
//
// Точность контролируется числом сегментов на кривую (визуально неотличимо, а
// extrude принимает полигональные верты).

export const ARC_SEGMENTS = 64;      // сегментов на полную окружность
const WELD_EPS = 0.02;        // склейка вершин (мм) — узлы ближе сливаются
const QUANT = 1 / WELD_EPS;   // квантование координат для хеш-слияния

// --- Квантование вершины в целочисленный ключ (слияние близких точек) ---
function keyOf(x, y) {
    return `${Math.round(x * QUANT)},${Math.round(y * QUANT)}`;
}

// --- Сбор всех кривых эскиза как полилиний (списки точек [{x,y}]) ---
// Возвращает массив полилиний; каждая — { pts:[{x,y}...], closed:boolean }.
/*
 * Отбросить линии, которые ФИЗИЧЕСКИ не могут быть частью замкнутого контура.
 *
 * 🔴 ЗАЧЕМ. Линию разметки («отступ 20 мм от края») рисуют обычным
 * инструментом и ведут от края детали к центру будущего отверстия. Для
 * разбиения она — полноправное ребро, поэтому отрезала от пластины лишнюю
 * область, а если проходила через окружность, то делила её на дольки.
 * Итог: «Выдавить» подсвечивал и выдавливал полосу вместо отверстия
 * (замерено на эскизе урока 7: вместо «4000 + 50 + 50» получалось
 * «3462 + 487 + 50 + 38 + 13»).
 *
 * ПРИЗНАК СТРОГИЙ, НЕ ЭВРИСТИКА. У замкнутого контура в каждой вершине
 * сходятся минимум два ребра. Значит линия, у которой хотя бы один конец
 * «висит» (в нём нет второй линии и он не лежит на окружности/дуге), не
 * может входить ни в один контур — её вклад в разбиение всегда паразитный.
 *
 * Обрезаем ИТЕРАТИВНО: убрав одну линию, можно оставить висячим конец
 * соседней (цепочка разметки из нескольких сегментов) — повторяем, пока
 * есть что убирать. Это классическая обрезка «листьев» графа.
 *
 * Проверено: прямоугольник, треугольник, L-контур, две смежные комнаты с
 * общей стеной и контур с дугой — не пострадали.
 */
function pruneDanglingLines(prims) {
    const pointById = new Map(
        prims.filter((p) => p.kind === 'point').map((p) => [p.id, p])
    );
    const circles = prims.filter((p) => p.kind === 'circle');
    const arcs = prims.filter((p) => p.kind === 'arc');

    // Конец «занят», если лежит НА окружности или является концом дуги:
    // такая точка участвует в контуре через кривую, а не через линию.
    const onCurve = (pt) => {
        if (!pt) return false;
        for (const c of circles) {
            if (Math.abs(Math.hypot(pt.x - c.cx, pt.y - c.cy) - c.r) < 1e-6) return true;
        }
        for (const a of arcs) {
            if (a.p1 === pt.id || a.p2 === pt.id) return true;
        }
        return false;
    };

    let lines = prims.filter((p) => p.kind === 'line');
    const others = prims.filter((p) => p.kind !== 'line');
    let changed = true;
    while (changed) {
        changed = false;
        const deg = new Map();
        for (const l of lines) {
            deg.set(l.p1, (deg.get(l.p1) || 0) + 1);
            deg.set(l.p2, (deg.get(l.p2) || 0) + 1);
        }
        const keep = lines.filter((l) => {
            for (const end of [l.p1, l.p2]) {
                if ((deg.get(end) || 0) < 2 && !onCurve(pointById.get(end))) return false;
            }
            return true;
        });
        if (keep.length !== lines.length) { lines = keep; changed = true; }
    }
    return [...others, ...lines];
}


function collectPolylines(sketch) {
    const prims = pruneDanglingLines(sketch.primitives || []);
    const pointById = new Map(
        prims.filter((p) => p.kind === 'point').map((p) => [p.id, p])
    );
    const polylines = [];

    for (const p of prims) {
        /*
         * 🔴 ВСПОМОГАТЕЛЬНАЯ ГЕОМЕТРИЯ КОНТУР НЕ ОБРАЗУЕТ И НЕ РЕЖЕТ ЕГО.
         *
         * Осевые и разметочные линии (construction) существуют, чтобы к ним
         * привязываться, а не чтобы делить фигуры. Раньше они попадали сюда
         * наравне с обычными, и findRegions резал по ним эскиз: линия через
         * центр окружности делила её на ДВА ПОЛУКРУГА, и в ядро вместо
         * отверстия уходила половинка, замкнутая хордой по диаметру.
         *
         * Именно так ломался урок 2: профиль приезжал с 33 вершинами и
         * площадью 156.83 мм² — ровно половина круга Ø20 (полная 313.65).
         * Отверстия в детали не появлялось, и проверка честно говорила
         * «отверстий 0».
         *
         * Старый алгоритм профилей (sketchProfiles.js) construction отфильтровывал
         * всегда — здесь фильтра просто не было.
         */
        if (p.construction) continue;

        if (p.kind === 'line') {
            const a = pointById.get(p.p1), b = pointById.get(p.p2);
            if (!a || !b) continue;
            polylines.push({ pts: [{ x: a.x, y: a.y }, { x: b.x, y: b.y }], closed: false });
        } else if (p.kind === 'arc') {
            const a = pointById.get(p.p1), b = pointById.get(p.p2);
            if (!a || !b) continue;
            polylines.push({ pts: tessellateArcPts(p, a, b), closed: false });
        } else if (p.kind === 'circle') {
            polylines.push({ pts: tessellateCirclePts(p), closed: true });
        }
    }
    return polylines;
}

function tessellateCirclePts(c) {
    const out = [];
    for (let i = 0; i < ARC_SEGMENTS; i++) {
        const t = (i / ARC_SEGMENTS) * Math.PI * 2;
        out.push({ x: c.cx + Math.cos(t) * c.r, y: c.cy + Math.sin(t) * c.r });
    }
    return out; // без замыкающей точки — closed=true обходит по кругу
}

function tessellateArcPts(arc, a, b) {
    const { cx, cy } = arc;
    const r = Math.hypot(a.x - cx, a.y - cy);
    const a0 = Math.atan2(a.y - cy, a.x - cx);
    const a1 = Math.atan2(b.y - cy, b.x - cx);
    let ccw;
    if (typeof arc.ccw === 'boolean') ccw = arc.ccw;
    else { let s = a1 - a0; while (s <= -Math.PI) s += 2*Math.PI; while (s > Math.PI) s -= 2*Math.PI; ccw = s >= 0; }
    let span = a1 - a0;
    if (ccw) { while (span <= 0) span += 2*Math.PI; while (span > 2*Math.PI) span -= 2*Math.PI; }
    else     { while (span >= 0) span -= 2*Math.PI; while (span < -2*Math.PI) span += 2*Math.PI; }
    const segs = Math.max(2, Math.ceil(ARC_SEGMENTS * Math.abs(span) / (2*Math.PI)));
    const out = [{ x: a.x, y: a.y }];
    for (let i = 1; i < segs; i++) {
        const t = a0 + span * (i / segs);
        out.push({ x: cx + Math.cos(t) * r, y: cy + Math.sin(t) * r });
    }
    out.push({ x: b.x, y: b.y });
    return out;
}

// --- Пересечение двух отрезков p1p2 и p3p4. Возвращает {x,y,t,u} или null ---
// t — параметр на первом отрезке (0..1), u — на втором.
function segIntersect(p1, p2, p3, p4) {
    const d1x = p2.x - p1.x, d1y = p2.y - p1.y;
    const d2x = p4.x - p3.x, d2y = p4.y - p3.y;
    const denom = d1x * d2y - d1y * d2x;
    if (Math.abs(denom) < 1e-12) return null; // параллельны/коллинеарны
    const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / denom;
    const u = ((p3.x - p1.x) * d1y - (p3.y - p1.y) * d1x) / denom;
    const E = 1e-9;
    if (t < -E || t > 1 + E || u < -E || u > 1 + E) return null;
    return { x: p1.x + d1x * t, y: p1.y + d1y * t, t, u };
}

// --- Построить набор уникальных неориентированных рёбер после дробления ---
// Каждая полилиния → её отрезки; каждый отрезок дробится точками пересечения
// с ВСЕМИ другими отрезками. Рёбра дедуплицируются по ключам концов.
function buildEdges(polylines) {
    // Разворачиваем полилинии в плоский список отрезков.
    const segs = [];
    for (const pl of polylines) {
        const n = pl.pts.length;
        const last = pl.closed ? n : n - 1;
        for (let i = 0; i < last; i++) {
            const a = pl.pts[i], b = pl.pts[(i + 1) % n];
            if (Math.hypot(b.x - a.x, b.y - a.y) < 1e-9) continue;
            segs.push({ a, b });
        }
    }

    // Для каждого отрезка собираем точки разбиения (его концы + пересечения).
    const splitsPerSeg = segs.map((s) => [
        { x: s.a.x, y: s.a.y, t: 0 },
        { x: s.b.x, y: s.b.y, t: 1 },
    ]);
    for (let i = 0; i < segs.length; i++) {
        for (let j = i + 1; j < segs.length; j++) {
            const hit = segIntersect(segs[i].a, segs[i].b, segs[j].a, segs[j].b);
            if (!hit) continue;
            // Пропускаем пересечения ровно в общих концах (уже есть как t=0/1).
            splitsPerSeg[i].push({ x: hit.x, y: hit.y, t: hit.t });
            splitsPerSeg[j].push({ x: hit.x, y: hit.y, t: hit.u });
        }
    }

    // Вершины (со слиянием по квантованию) → индекс.
    const vertKey = new Map();  // key → vertexIndex
    const verts = [];           // [{x,y}]
    const vindex = (x, y) => {
        const k = keyOf(x, y);
        let idx = vertKey.get(k);
        if (idx == null) { idx = verts.length; verts.push({ x, y }); vertKey.set(k, idx); }
        return idx;
    };

    // Неориентированные рёбра (dedup по паре индексов).
    const edgeKey = new Set();
    const edges = []; // [{u, v}]
    for (let i = 0; i < segs.length; i++) {
        const list = splitsPerSeg[i].sort((p, q) => p.t - q.t);
        let prev = null;
        for (const sp of list) {
            const vi = vindex(sp.x, sp.y);
            if (prev != null && prev !== vi) {
                const k = prev < vi ? `${prev}_${vi}` : `${vi}_${prev}`;
                if (!edgeKey.has(k)) { edgeKey.add(k); edges.push({ u: prev, v: vi }); }
            }
            prev = vi;
        }
    }
    return { verts, edges };
}

// --- Обход минимальных граней планарного графа (DCEL) ---
// Для каждого направленного полу-ребра next = самый «правый» поворот в конечной
// вершине (минимальный угол по часовой) — так замыкаются минимальные грани.
function traceFaces(verts, edges) {
    if (edges.length === 0) return [];

    // Список полу-рёбер: каждое ребро даёт два (u→v и v→u).
    const halfedges = [];
    const outgoing = new Map(); // vertexIndex → [halfedgeIndex,...]
    const addHE = (from, to) => {
        const idx = halfedges.length;
        const dx = verts[to].x - verts[from].x;
        const dy = verts[to].y - verts[from].y;
        halfedges.push({ from, to, angle: Math.atan2(dy, dx), visited: false });
        if (!outgoing.has(from)) outgoing.set(from, []);
        outgoing.get(from).push(idx);
        return idx;
    };
    for (const e of edges) { addHE(e.u, e.v); addHE(e.v, e.u); }
    // twin(i): рёбра добавлены парами (2k, 2k+1).
    const twin = (i) => (i % 2 === 0 ? i + 1 : i - 1);

    // Отсортируем исходящие полу-рёбра каждой вершины по углу (для поиска next).
    for (const [, list] of outgoing) list.sort((i, j) => halfedges[i].angle - halfedges[j].angle);

    // next(he): в вершине he.to берём twin(he), затем следующее по часовой
    // стрелке исходящее ребро → минимальная грань слева.
    const nextHE = (heIdx) => {
        const he = halfedges[heIdx];
        const t = twin(heIdx);              // входим в he.to, разворачиваемся
        const list = outgoing.get(he.to);
        if (!list || list.length === 0) return -1;
        // Ищем позицию twin в отсортированном списке исходящих he.to.
        const pos = list.indexOf(t);
        if (pos < 0) return -1;
        // Следующий по часовой = предыдущий по возрастанию угла (циклически).
        const prev = (pos - 1 + list.length) % list.length;
        return list[prev];
    };

    const faces = [];
    for (let s = 0; s < halfedges.length; s++) {
        if (halfedges[s].visited) continue;
        const loop = [];
        let cur = s, guard = 0;
        let ok = true;
        while (!halfedges[cur].visited) {
            halfedges[cur].visited = true;
            loop.push(halfedges[cur].from);
            cur = nextHE(cur);
            if (cur < 0) { ok = false; break; }
            if (++guard > halfedges.length + 5) { ok = false; break; }
        }
        if (!ok || loop.length < 3) continue;
        // Полигон вершин грани.
        const poly = loop.map((vi) => ({ x: verts[vi].x, y: verts[vi].y }));
        const area = signedArea(poly);
        if (Math.abs(area) < 1e-6) continue; // вырожденная петля
        faces.push({ poly, area });
    }

    if (faces.length === 0) return [];

    // Определяем внешнюю (неограниченную) грань. В DCEL при нашем обходе
    // (next = следующее ребро ПО ЧАСОВОЙ вокруг конечной вершины) все
    // ОГРАНИЧЕННЫЕ грани обходятся с одним знаком площади, а единственная
    // неограниченная — с противоположным. Знак зависит от направления оси Y
    // эскиза, поэтому не хардкодим: внешняя грань — та, чей знак площади в
    // МЕНЬШИНСТВЕ (обычно ровно одна). Дополнительно она объемлет остальные,
    // поэтому среди «меньшинства» берём с максимальной |площадью|.
    let pos = 0, neg = 0;
    for (const f of faces) { if (f.area > 0) pos++; else neg++; }
    const extSignPositive = pos <= neg; // внешняя — там, где знаков меньше
    let extIdx = -1, extA = -1;
    for (let i = 0; i < faces.length; i++) {
        const isPos = faces[i].area > 0;
        if (isPos === extSignPositive) {
            const aa = Math.abs(faces[i].area);
            if (aa > extA) { extA = aa; extIdx = i; }
        }
    }

    const inner = [];
    /*
     * 🔴 ОДНА И ТА ЖЕ ГРАНЬ НЕ ДОЛЖНА ПОПАСТЬ В СПИСОК ДВАЖДЫ.
     *
     * У контура, НЕ СОЕДИНЁННОГО с остальным эскизом (отдельно стоящая
     * окружность — типичное отверстие), обход даёт ДВЕ грани: внутреннюю и
     * внешнюю. Внешней отбрасывается только одна на весь эскиз, поэтому у
     * каждого изолированного круга оставался лишний дубль, а после
     * нормализации к CCW оба контура становились ОДИНАКОВЫМИ — вплоть до
     * совпадения id (он строится из центроида и площади).
     *
     * Последствия были неприятные и неочевидные: React получал два элемента
     * с одним key, а selection.ids не мог различить эти профили — выбиралось
     * не то, что подсвечено под курсором.
     *
     * Дедупликация по центроиду и площади: настоящие разные грани так
     * совпасть не могут (у них либо центр, либо площадь отличаются).
     */
    const seen = new Set();
    for (let i = 0; i < faces.length; i++) {
        if (i === extIdx) continue;
        const f = faces[i];
        // Нормализуем ориентацию вершин к CCW (положительная площадь).
        const poly = f.area < 0 ? f.poly.slice().reverse() : f.poly;
        const area = Math.abs(f.area);
        let sx = 0, sy = 0;
        for (const v of poly) { sx += v.x; sy += v.y; }
        const key = Math.round(sx / poly.length * 1000) + ':'
            + Math.round(sy / poly.length * 1000) + ':'
            + Math.round(area * 1000);
        if (seen.has(key)) continue;
        seen.add(key);
        inner.push({ poly, area });
    }
    return inner;
}

function signedArea(poly) {
    let a = 0;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        a += (poly[j].x + poly[i].x) * (poly[j].y - poly[i].y);
    }
    return a / 2;
}

/**
 * Главная функция: построить профили-грани эскиза через планарное разбиение.
 * Возвращает массив профилей вида:
 *   { id, kind:'region', verts:[{x,y}...], area }
 * verts — замкнутый контур (без повтора первой точки).
 *
 * id стабилен для одного и того же набора кривых (хеш округлённого центроида
 * + площади) — чтобы выбор/подсветка не «прыгали» между кадрами.
 */
export function findRegions(sketch) {
    if (!sketch || !sketch.primitives) return [];
    const polylines = collectPolylines(sketch);
    if (polylines.length === 0) return [];
    const { verts, edges } = buildEdges(polylines);
    const faces = traceFaces(verts, edges);

    return faces.map((f) => {
        // Центроид для стабильного id и hit-приоритета.
        let sx = 0, sy = 0;
        for (const v of f.poly) { sx += v.x; sy += v.y; }
        const cx = sx / f.poly.length, cy = sy / f.poly.length;
        const id = `region_${Math.round(cx * 100)}_${Math.round(cy * 100)}_${Math.round(f.area)}`;
        return {
            id,
            kind: 'region',
            verts: f.poly,
            area: f.area,
            center: { x: cx, y: cy },
        };
    });
}

// Точка внутри региона (ray casting).
export function pointInRegion(region, x, y) {
    const verts = region.verts;
    if (!verts || verts.length < 3) return false;
    let inside = false;
    for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
        const xi = verts[i].x, yi = verts[i].y;
        const xj = verts[j].x, yj = verts[j].y;
        const intersect = (yi > y) !== (yj > y) &&
            (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
}
